//import { redirectTo } from "@gatsbyjs/reach-router";
import axios, { AxiosRequestConfig } from "axios";
import { action, makeObservable, observable, runInAction, toJS } from "mobx";
import dayjs from "dayjs";
import { message } from "antd";
import _ from "lodash";
import { v4 as uuidv4 } from "uuid";
import { config } from "../config/configUi.js";
import { getCookieValue, setCookieValue } from "../utils/helpers";
import { loadLanguageJSON, translateKey as t } from "../locales/translation";

export class AppStore {
  public accessToken: any = null;
  static instance: any;

  public language;
  public loadingUserProfile = false;
  public isRedirecting = false;
  public authenticated = false;
  public authorizationErrorMsg = "";
  public user: any = {};
  public needsRelogin = false;
  public notifications: any = [];
  public notificationsPopover: any = [];
  public newNotifications = 0;
  public muteNotifications = localStorage.getItem("OF-muteNotifications") || dayjs().toISOString();
  public showClientLogos = true;
  public showPartnerLogos = true;
  public enableConsoleLogs = false;
  public debugModeReminder = false;
  public clients: any = [];
  public selectedClient = null;
  public clientSpecificMode = false;
  public clientsSocket: any = null;

  public clientSettings = null;
  public clientPreferences = null;
  public pageSettings = null;
  public userPreferences = JSON.parse(localStorage.getItem("userPreferences")) || {
    pageSettings: { most: [], recent: [], favorites: {} },
  };
  public loadingUserPreferences = false;
  public grayscale = null;
  public statusBar = {
    userName: null,
    visible: true,
    wsConnected: false,
    wsUserDisabled: localStorage.getItem("wsUserDisabled") === "true",
    ofConnected: false,
    connectedDepot: null,
    progressBar: {
      active: false,
      mode: "indeterminate",
      progressValue: 0,
      progressBuffer: 0,
      message: null,
    },
  };

  public preLoaderProps = {
    loading: false,
    loadingText: "",
  };
  public verticals = [];

  static getInstance() {
    this.instance = this.instance || new AppStore();
    return this.instance;
  }

  constructor() {
    makeObservable(this, {
      loadingUserProfile: observable,
      notifications: observable,
      notificationsPopover: observable,
      newNotifications: observable,
      clientSettings: observable.shallow,
      clientPreferences: observable.shallow,
      pageSettings: observable,
      userPreferences: observable,
      loadingUserPreferences: observable,
      language: observable,
      showClientLogos: observable,
      grayscale: observable,
      muteNotifications: observable,
      debugModeReminder: observable,
      statusBar: observable,
      clients: observable,
      selectedClient: observable,
      clientSpecificMode: observable,
      verticals: observable,

      preLoaderProps: observable,
      setPreLoaderProps: action,
    });

    // Check the users authentication.
    this.checkAuthentication();

    // Load the prerequisite data.
    this.loadPrerequesites();

    // Get client logos cookie
    let showLogos = window.localStorage.getItem("showClientLogos");
    if (showLogos) {
      this.showClientLogos = showLogos === "true" ? true : false;
    }

    this.checkGrayscale();
  }

  //-----------------------------------------------------------------

  public setPreLoaderProps(value: any) {
    this.preLoaderProps = value;
  }

  //-----------------------------------------------------------------

  loadPrerequesites = async () => {
    this.language = this.getUserPreferences("language", "en");

    //this.loadUserRouteSettings()
    loadLanguageJSON(this.language);

    const enableLogs = this.loadUserGlobalSetting("enableConsoleLogs");

    // If the user is not in dev env and logged, check for the debug mode expiration and show reminder if expired
    const showDebugModal = process.env.NODE_ENV !== "development" && this.authenticated && window.location.href.indexOf("callback_success") === -1;

    // Check if the cookie exists
    if (enableLogs) {
      // If it does, convert it back to date
      const timeRemaining = dayjs(Number(this.loadUserGlobalSetting("enableConsoleLogs")));

      // If the date is valid
      if (timeRemaining.isValid()) {
        // If the cookie timestamp is before todays time, show the popup
        if (dayjs(timeRemaining.toDate()).isBefore(dayjs())) {
          this.debugModeReminder = showDebugModal;
        } else {
          this.enableConsoleLogs = true;
        }
      } else {
        this.debugModeReminder = showDebugModal;
      }
    } else {
      this.enableConsoleLogs = false;
    }

    if (this.authenticated) {
      //get Provider Clients
      // const clientsData = await this.getData("clients", "pro_client");
      // runInAction(() => {
      //   if (this.user.roles.includes("DEMO_USER")) {
      //     this.clients = clientsData.filter((client) => client.proOptions?.demo);
      //   } else {
      //     this.clients = clientsData;
      //   }

      //   this.clients.unshift({ clientRef: "generic", name: "All Clients" });
      // });
      // this.consoleLog("Clients", this.clients);

      // // IFC - Only load report definitions for enterprise not pro.
      // if (config.platform !== "openfleet-ui-pro") {
      //   this.reportDefinitions = JSON.parse(this.loadUserGlobalSetting("reportDefinitions"));
      //   if (!this.reportDefinitions) {
      //     this.createReports();
      //   }
      // }

      // // IFC - Added to also load charts, as these were not being loaded before.
      // if (config.platform !== "openfleet-ui-pro") {
      //   this.chartDefinitions = JSON.parse(this.loadUserGlobalSetting("chartDefinitions"));
      //   if (!this.chartDefinitions) {
      //     this.createCharts();
      //   }
      // }

      // // Get the UI Client Settings.
      // this.getData("client-settings", "main", "/login").then((clientSettings) => {
      //   // Set the client settings.
      //   this.clientSettings = (clientSettings && clientSettings.length > 0 && clientSettings[0]) || {};

      //   //this.initAdditionalSettings();
      // });

      // // Get the UI Client Preferences.
      // this.getData("ui-client-preferences").then((clientPreferences) => {
      //   if (clientPreferences) {
      //     // Set the UI Client Preferences.
      //     this.clientPreferences = clientPreferences;

      //     setCookieValue("clientPreferences", clientPreferences);

      //     // this.clientPreferences = (clientPreferences && clientPreferences.length > 0 && clientSettings[0]) || {}
      //     this.consoleLog("Client Preferences : ", clientPreferences);
      //   }
      // });

      // this.ofSocket = new OFSocket(this)
      // this.ofSocket.connectSocket()

      this.statusBar.visible = window.location.pathname === "/mobileAdmin" ? false : true;
    }
  };

  /**
   * Validate the authentication if the request is a google one.
   */
  private validateAltAuth = (provider = "google") => {
    // Get the URL params.
    const urlParams = new URLSearchParams(window.location.search);
    // Get the nonce from query string.
    const nonce_query = decodeURIComponent(urlParams.get("of_lognonce") ?? "");
    // Get the nonce from local storage.
    const nonce_local = decodeURIComponent(window.localStorage.getItem("of_lognonce") ?? "");
    // Check if the nonce exists.
    if (!nonce_query || !nonce_local) {
      // Log.
      console.log("Nonce not matched, login again please");
      // Error message.
      const msg = "Auth Failed (ERR : NCEMISS)";
      // Redirect to login.
      window.location.href = "/login?msg=" + msg;
    }
    // Check if nonces match and if so contiune.
    else if (nonce_local === nonce_query) {
      // Log.
      console.log("Nonce matched : ", nonce_local);
      // Remove nonce.
      window.localStorage.removeItem("of_lognonce");

      // Check if the session id exists.
      if (urlParams.has("session_id")) {
        // Call the post sign in & authenticate.
        this.validateSession(urlParams.get("session_id"), provider);
      } else {
        // Error message.
        const msg = "Auth Failed (ERR : MISSINGSESSIONID)";
        // Redirect to login.
        window.location.href = "/login?msg=" + msg;
      }
    }
    // Otherwise redreict to login again.
    else {
      // Log.
      console.log("nonce_query : ", nonce_query);
      console.log("nonce_local : ", nonce_local);
      // Error message.
      const msg = "Auth Failed (ERR : NCEMTCH)";
      // Redirect to login.
      window.location.href = "/login?msg=" + msg;
    }

    // Return.
    return;
  };

  private checkAuthentication = (redirect = false, message: any = null) => {
    // If this funcion is called from any api response with 401 (unauthorised)
    if (redirect) {
      this.authenticated = false;
      this.authorizationErrorMsg = message?.data?.message ? message.data.message : "Your session has expired";
    }

    // Check if the page is callback on google, if so add required handling.
    if (window.location.pathname === "/google_callback_success" || window.location.pathname === "/azure_callback_success") {
      let provider = "google";
      switch (window.location.pathname) {
        case "/google_callback_success":
          provider = "google";
          break;
        case "/azure_callback_success":
          provider = "azure";
          break;
        default:
          provider = "google";
      }
      this.validateAltAuth(provider);
    } else {
      // Get the user from local storage.
      let user = window.localStorage.getItem("pro_userInfo") && JSON.parse(window.localStorage.getItem("pro_userInfo") as string);

      // Check the expiry of the token.
      if (user && !redirect) {
        // Snap the user object.
        this.user = user;
        this.selectedClient = user.selectedClient;
        // Check if the token is expired.
        if (dayjs().valueOf() <= this.user.expires) {
          // Set to authenticated.
          this.authenticated = true;

          //this.showPostLoginNotification();
        } else {
          this.authorizationErrorMsg = "Session Expired. Please login again.";
        }
      }
    }

    // Otherwise check standard authentication.
    if (
      !this.needsRelogin &&
      !this.authenticated &&
      window.location.pathname !== "/login" &&
      window.location.pathname !== "/" &&
      window.location.pathname !== "/popup" &&
      window.location.pathname !== "/online/signup" &&
      window.location.pathname !== "/online/forgot" &&
      window.location.pathname !== "/online/resetPassword" &&
      window.location.pathname !== "/google_callback_success" &&
      window.location.pathname !== "/azure_callback_success" &&
      !window.location.pathname.endsWith("/signin")
    ) {
      let path = window.location.pathname;

      if (path.indexOf("&msg=") > 0) {
        let pathArray = path.split("&msg=");

        // Redirect back to login.
        window.location.href = "/login?reqUrl=" + pathArray[0] + "&msg=" + pathArray[1];
      } else {
        // Redirect back to login.
        window.location.href = "/login?reqUrl=" + path + "&msg=" + this.authorizationErrorMsg;
      }
    }
  };

  private validateSession = async (sessionID: any, provider = "google") => {
    // Prepare the payload.
    const payload = {
      session_id: sessionID,
    };

    try {
      // Try to sign in.
      const response = await this.postData("auth/validate", payload);
      console.log("Validate Session Result:", JSON.stringify(response));

      // Post SingIn.
      this.postSignIn(response.username, response.client_ref, response, provider);
    } catch (err: any) {
      // Log Error.
      console.log("Validate Session Error:", JSON.stringify(err));
      // Error message.
      let msg = "Auth Failed (ERR : SESSION)";
      // If the error is a 400, then the api had issues.
      if (err.response && err.response.status === 400) {
        msg = "Auth Failed (ERR : API Session Validate)";
      }
      // If error is a 401, then the session is invalid.
      else if (err.response && err.response.status === 401) {
        msg = "Auth Failed (Invalid Session ID)";
      }
      // Redirect to login.
      window.location.href = "/login?msg=" + msg;
    }
  };

  public postSignIn = async (username: string, clientRef: string, loginResponse: any, authMethod = "local") => {
    // Snap the new access token to the call.
    this.user.accessToken = loginResponse.accessToken;

    console.log("PRO Manage Token: ", this.user.accessToken);
    // Welcome text.
    // const usernameTxt = username ? username.toUpperCase() : "N/A";
    // Log
    // this.startOverlayLoader({
    //   description: 'Welcome to openFleet (' + usernameTxt + ')',
    //   subDescription: 'Fetching your user information & preferences...'
    // })

    let ofTokenResp;

    this.isRedirecting = false;

    // Set the auth in progress flag.
    this.loadingUserProfile = true;

    try {
      //get openfleet token
      ofTokenResp = await this.postData("auth/of-token", {});
    } catch (err) {
      this.loadingUserProfile = false;
      //const errMessage = "Failed connection to Openfleet Provider!";
      const errMessage = err.response?.data?.message || err.message || err;

      console.error("Error getting OF Token: ", errMessage);
      window.location.href = "/login?msg=" + errMessage;
      return;
    }

    try {
      this.user.ofProviderUrl = ofTokenResp.providerUrl;

      // Setup the post sign in.
      const userInfo = await this.getData("users/login/info?username=" + username);

      // Log
      console.log("OF Login - User Info : ", userInfo);
      // Set user.
      this.user = userInfo.user;
      this.user.clientRef = clientRef.toUpperCase();
      this.user.routes = userInfo.userRoutes;
      this.user.accessToken = loginResponse.accessToken;
      this.user.ofAccessToken = ofTokenResp.accessToken;
      this.user.ofProviderUrl = ofTokenResp.providerUrl;
      this.user.tokenVirtualPath = ofTokenResp.tokenVirtualPath;
      this.user.ofEnvType = ofTokenResp.envType;
      // assign expires to which token expires first
      this.user.expires = loginResponse.expires < ofTokenResp.expires ? loginResponse.expires : ofTokenResp.expires;
      this.user.inactivityTimeout = loginResponse.inactivityTimeout;
      this.user.inactivityTimeoutExpires = dayjs().add(loginResponse.inactivityTimeout, "milliseconds");
      this.user.permissions = userInfo.permissions || [];
      // Save the userinfo.
      setCookieValue("pro_userInfo", this.user);

      //this.initUserPreferences(userInfo);

      // Parse query string.
      const urlParams = new URLSearchParams(window.location.search);
      let transitionUrl = "";

      // Add post notification to the storage so we could access it after redirection
      if (loginResponse.message) {
        setCookieValue("postLoginMessage", {
          message: loginResponse.message,
          messageType: loginResponse.messageType,
        });
      }

      const reqUrl = decodeURIComponent(urlParams.get("reqUrl") || "");
      // Transition the page to req url or home.
      transitionUrl = reqUrl;

      if (transitionUrl) {
        this.isRedirecting = true;
        window.location.href = transitionUrl;
      } else {
        this.showPostLoginNotification();
      }

      // Log.
      //this.stopOverlayLoader();

      // Set to authenticated.
      this.authenticated = true;

      window.location.href = "/configuration/clients";
      // Load prereqs.
      //this.loadPrerequesites();
    } catch (err) {
      // Reset flag.
      this.loadingUserProfile = false;
      // Log.
      console.log("Error Loading User Info :", err);
      // Stop the overlay.
      // this.stopOverlayLoader()
      // Display message.
      this.displayNotification("Error Loading User Info", "error", true);
      // Prepare message.
      const errMessage = "Error Loading User Info";
      // Go back to login.
      window.location.href = "/login?msg=" + errMessage;
    }
  };

  public setClient = async (clientRef) => {
    const ofTokenResp = await this.postData("auth/of-token", { clientRef: clientRef });

    this.clientSpecificMode = true;
    this.selectedClient = clientRef;
    this.user.ofAccessTokenClient = ofTokenResp.accessToken;
    this.user.selectedClient = clientRef;
    setCookieValue("pro_userInfo", this.user);
  };

  public clearClient = () => {
    this.clientSpecificMode = false;
    this.selectedClient = null;
    this.user.ofAccessTokenClient = null;
    this.user.selectedClient = null;
    setCookieValue("pro_userInfo", this.user);
  };

  public signOut = (redirect = true, redirectMsg = null, reLogin = false) => {
    redirect = true; // Force redirect for now

    localStorage.removeItem("pro_userInfo");
    this.getData("auth/signout").then((resp) => {
      this.consoleLog("LOGGED OUT API:", resp);
      this.authenticated = false;

      if (this.user.provider === "azuread_deprecated") {
        this.consoleLog("destroySessionURL");
        window.location = this.clientSettings.destroySessionUrl;
      } else {
        if (redirect) {
          const path = window.location.pathname.toLowerCase();
          if (path !== "/login" && redirectMsg !== null) {
            let errorMsg = "";
            if (redirectMsg) {
              errorMsg = "&msg=" + redirectMsg;
            }
            window.location.href = window.location.pathname + "" + errorMsg;
          } else {
            let errorMsg = "";
            if (redirectMsg) {
              errorMsg = "&msg=" + redirectMsg;
            }

            if (reLogin) {
              window.location.href = "/login?reLogin=true&reqUrl=" + window.location.pathname;
            } else {
              window.location.href = "/login" + errorMsg;
            }
          }
        } else {
          this.needsRelogin = true;
        }
      }
    });
  };

  private showPostLoginNotification = () => {
    const postLoginMessage = localStorage.getItem("postLoginMessage");
    const getNotification = postLoginMessage && JSON.parse(postLoginMessage);

    if (getNotification) {
      this.displayNotification(getNotification.message, getNotification.messageType, true);
    }

    localStorage.removeItem("postLoginMessage");
  };

  /**
    For the long informative notifications, use persist
    as they will be saved in notification tray, for everything
    else short popup message will show up and will not be saved
    anywhere.
  */
  public displayNotification = (msg: string, type: any, persist: any, desc = null, bg = "#fff") => {
    if (persist) {
      // if (!this.notificationsMuted()) {
      //   let descLimit = desc

      //   if (desc && desc.length > 250) {
      //     descLimit = desc.substring(0, 250) + ' ...'
      //   }

      //   // Display notification
      //   notification[type]({
      //     // style: { fontWeight: 500, background: bg },
      //     placement: 'topRight',
      //     description: descLimit,
      //     message: msg,
      //     className: 'notification',
      //     // getContainer: () => document.getElementsByClassName('notificationContainer')[0],
      //     duration: null,
      //     top: 70
      //   })
      // }

      // this.notifications.unshift({
      //   timestamp: moment().format('HH:mm:ss'),
      //   description: desc,
      //   message: msg,
      //   persist,
      //   type
      // })

      const notification = {
        timestamp: dayjs().format("HH:mm:ss"),
        description: desc,
        message: msg,
        persist,
        type,
        id: uuidv4(),
        animated: true,
      };

      // Store in notification tray
      this.notifications.unshift(notification);
      this.notificationsPopover.unshift(notification);

      // Counter to display new notifications
      this.newNotifications++;
    } else {
      message.config({ top: 120 });
      (message as any)[type](msg);
    }
  };

  // Check if notifications are muted
  public notificationsMuted = () => {
    if (dayjs().isBefore(dayjs(this.muteNotifications))) {
      return true;
    }

    return false;
  };

  public getUserPreferences = (key, defaultValue = {}) => {
    return _.get(this.userPreferences, key, defaultValue);
  };

  // Update (add or remove) user settings with different configuration or updated data
  public updateUserPreferences = async (key, value, silent = false) => {
    this.loadingUserPreferences = true;

    const payload = toJS(this.userPreferences);

    _.set(payload, key, value);
    _.set(payload, "userId", this.user._id);

    await this.postData("ui-user-preferences/" + this.user._id, payload)
      .then(() => {
        _.set(this.userPreferences, key, value);

        setCookieValue("userPreferences", this.userPreferences);
      })
      .catch(() => {
        !silent && this.displayNotification(t("lblFailedToSaveUserPreferences", "Failed To Save User Preferences"), "error", true);
      });

    this.loadingUserPreferences = false;
  };

  public loadUserGlobalSetting = (settingKey) => {
    let key = "userGlobalSetting_" + settingKey;
    let globalSettings = localStorage.getItem(key);
    return globalSettings;
  };

  public consoleLog = (message, object = {}, type = "normal") => {
    if (this.enableConsoleLogs) {
      switch (type) {
        case "warn": {
          if (object === null || (Object.entries(object).length === 0 && object.constructor === Object)) {
            console.warn(message);
          } else {
            console.warn(message, object);
          }
          break;
        }
        case "error": {
          if (object === null || (Object.entries(object).length === 0 && object.constructor === Object)) {
            console.error(message);
          } else {
            console.error(message, object);
          }
          break;
        }
        default: {
          if (object === null || (Object.entries(object).length === 0 && object.constructor === Object)) {
            console.log(message);
          } else {
            console.log(message, object);
            break;
          }
        }
      }
    }
  };

  // public getCustomLogo = (clientLogo = true) => {
  //   let logo = null;
  //   let logoType = "clientLogo";
  //   let allowDevLogo = true;

  //   if (!clientLogo) {
  //     logoType = "partnerLogo";
  //   } else {
  //     // Check flag.
  //     if (allowDevLogo) {
  //       // Check if you are running locally.
  //       if (process.env.NODE_ENV !== "production") {
  //         // Check the config to see if overrides are enabled, then we assume you are in debug mode.
  //         if (config.configOverride) {
  //           if (config.environmentName === "Azure Dev") {
  //             logoType = "devLogo";
  //           } else {
  //             logoType = "debugLogo";
  //           }
  //         }
  //       }
  //       // If there is no config override, assume it's DEV mode.
  //       if (!config.configOverride) {
  //         logoType = "devLogo";
  //       }
  //     }
  //   }

  //   try {
  //     logo = require("~/images/customLogo/" + logoType + ".svg");
  //   } catch (ex) {
  //     try {
  //       logo = require("~/images/customLogo/" + logoType + ".png");
  //     } catch (ex) {
  //       // console.log('Error importing ' + clientLogo)
  //     }
  //   }

  //   return logo;
  // };

  public changeLanguage = async (lang) => {
    this.language = lang;
    // this.saveUserGlobalSetting('language', lang)
    await this.updateUserPreferences("language", lang);

    window.location.reload();
  };

  public checkGrayscale = () => {
    const grayscale: any = localStorage.getItem("grayscale");

    if (grayscale) {
      this.grayscale = parseInt(grayscale);

      document.body.style.filter = `grayscale(${grayscale / 5})`;
    }
  };

  public getPageStats = (limit = 3) => {
    // If the cookies exists
    const pageSettings = toJS(this.getUserPreferences("pageSettings"));

    let userRoutes = _.get(this.user, "routes", null);

    // If cookies exist for the current user, return the data
    if (pageSettings.most && pageSettings.recent) {
      // Map the page stats from cookies with original values in case the names were updated
      const mostVisited = pageSettings.most.filter((m) => {
        userRoutes.find((r: any) => {
          const ur = r.userRoutes.find((ur) => ur._id === m._id);

          if (ur) {
            m.name = ur.name;
            m.translationKey = ur.translationKey;
            m.routeGroup = r.name;
            m.routeGroupTranslationKey = r.translationKey;
            m.rights = ur.linkTo === m.path ? ur.rights : "";
            m.bypass = ur.bypass;
          }

          return null;
        });

        if (_.get(m, "bypass", false)) {
          return false;
        }

        // Check if the page has the rights to
        if (m.rights && m.rights.includes("R")) {
          return true;
        }

        return false;
      });

      // Map the page stats from cookies with original values in case the names were updated
      const mostRecent = pageSettings.recent.filter((m) => {
        userRoutes.forEach((r) => {
          const ur = r.userRoutes.find((ur) => ur._id === m._id);

          if (ur) {
            m.name = ur.name;
            m.translationKey = ur.translationKey;
            m.routeGroup = r.name;
            m.routeGroupTranslationKey = r.translationKey;
            m.rights = ur.linkTo === m.path ? ur.rights : "";
            m.bypass = ur.bypass;
          }
        });

        if (_.get(m, "bypass", false)) {
          return false;
        }

        // Check if the page has the rights to
        if (m.rights && m.rights.includes("R")) {
          return true;
        }

        return false;
      });

      return {
        most: _.orderBy(mostVisited, "count", ["desc"]).slice(0, limit),
        recent: _.orderBy(mostRecent, "timestamp", ["desc"]).slice(0, limit),
      };
    }

    // If no cookies exist for the current user, return null
    return null;
  };

  /**
    Call to get a client preference setting value.
    @param key - clientSetting key.
    @param defaultValue - the return value if the key isn't found.
    */
  public getClientPreference = (key, defaultValue = null, fromCookie = false) => {
    if (fromCookie) {
      return getCookieValue("clientPreferences", key, defaultValue);
    }

    return _.get(this.clientPreferences, key, defaultValue);
  };

  isApiWhitelisted = (api: string) => {
    const whitelist = ["login", "register", "signout", "verify", "forgot-password", "verify-password-reset", "reset-password"];

    if (whitelist.includes(api)) {
      return true;
    }

    return false;
  };

  async getData(queryString: string, api: string = "main", isClient: boolean = false, action: any = null) {
    let urlPath = window.location.pathname.toLowerCase();
    if (urlPath === "/") {
      urlPath = "/login";
    }

    const localUrls = ["127.0.0.1", "localhost"];
    const isLocalHost = localUrls.some((url) => this.user?.ofProviderUrl?.includes(url));

    const serverRoutingEnabled = this.user.tokenVirtualPath ? true : false;

    const apiAddressConfig = config.apiAddress[api];

    //for all apis except main
    let apiAddress = this.user.ofProviderUrl + apiAddressConfig.virtualPath;

    if (serverRoutingEnabled && api !== "pro_client") {
      apiAddress = this.user.ofProviderUrl + apiAddressConfig.suffixPath + apiAddressConfig.virtualPath;
    }

    //accessToken
    let accessToken = isClient ? this.user.ofAccessTokenClient : this.user.ofAccessToken;

    if (api === "main") {
      apiAddress = apiAddressConfig.url + apiAddressConfig.virtualPath;
      accessToken = this.user.accessToken;
    }

    if (isLocalHost) {
      apiAddress = apiAddressConfig.url + apiAddressConfig.virtualPath;
    }

    const axiosConfig: AxiosRequestConfig =
      !queryString.includes("auth/") || queryString === "auth/signout"
        ? {
            headers: {
              Authorization: "Bearer " + accessToken,
              Action: action || urlPath,
              "Accept-Language": "en",
              //'ClientId': this.user.client,
            },
          }
        : {};

    try {
      const { data } = await axios.get(apiAddress + queryString, axiosConfig);
      return data.data ?? data;
    } catch (error) {
      console.error(error);
      throw error;
    }
  }

  async postData(queryString: string, payload: any, api: string = "main", isClient: boolean = false, action: any = null) {
    let urlPath = window.location.pathname.toLowerCase();
    if (urlPath === "/") {
      urlPath = "/login";
    }

    const localUrls = ["127.0.0.1", "localhost"];
    let isLocalHost = localUrls.some((url) => this.user?.ofProviderUrl?.includes(url));

    const serverRoutingEnabled = this.user.tokenVirtualPath ? true : false;

    const apiAddressConfig = config.apiAddress[api];

    //for all apis except main
    //let apiAddress = apiAddressConfig.url + apiAddressConfig.virtualPath;
    let apiAddress = this.user.ofProviderUrl + apiAddressConfig.virtualPath;

    if (serverRoutingEnabled && api !== "pro_client") {
      apiAddress = this.user.ofProviderUrl + apiAddressConfig.suffixPath + apiAddressConfig.virtualPath;
    }

    //accessToken
    let accessToken = isClient ? this.user.ofAccessTokenClient : this.user.ofAccessToken;

    if (api === "main") {
      apiAddress = apiAddressConfig.url + apiAddressConfig.virtualPath;
      accessToken = this.user.accessToken;
    }

    if (isLocalHost) {
      apiAddress = apiAddressConfig.url + apiAddressConfig.virtualPath;
    }

    let axiosConfig: AxiosRequestConfig = {};

    //bypass auth for auth endpoints except for of-token
    if (!queryString.includes("auth/") || queryString === "auth/of-token") {
      axiosConfig = {
        headers: {
          Authorization: "Bearer " + accessToken,
          //Action: action || urlPath,
          "Accept-Language": "en",
          //'ClientId': this.user.client,
        },
      };
    }

    try {
      const { data } = await axios.post(apiAddress + queryString, payload, axiosConfig);
      return data;
    } catch (error) {
      console.error(error);
      throw error;
    }
  }

  async putData(queryString: string, payload: any, api: string = "main", isClient: boolean = false, action: any = null) {
    let urlPath = window.location.pathname.toLowerCase();
    if (urlPath === "/") {
      urlPath = "/login";
    }

    const localUrls = ["127.0.0.1", "localhost"];
    let isLocalHost = localUrls.some((url) => this.user?.ofProviderUrl?.includes(url));

    const serverRoutingEnabled = this.user.tokenVirtualPath ? true : false;

    const apiAddressConfig = config.apiAddress[api];

    //for all apis except main
    //let apiAddress = apiAddressConfig.url + apiAddressConfig.virtualPath;
    let apiAddress = this.user.ofProviderUrl + apiAddressConfig.virtualPath;

    if (serverRoutingEnabled && api !== "pro_client") {
      apiAddress = this.user.ofProviderUrl + apiAddressConfig.suffixPath + apiAddressConfig.virtualPath;
    }

    //accessToken
    let accessToken = isClient ? this.user.ofAccessTokenClient : this.user.ofAccessToken;

    if (api === "main") {
      apiAddress = apiAddressConfig.url + apiAddressConfig.virtualPath;
      accessToken = this.user.accessToken;
    }

    if (isLocalHost) {
      apiAddress = apiAddressConfig.url + apiAddressConfig.virtualPath;
    }

    let axiosConfig: AxiosRequestConfig = {};

    //bypass auth for auth endpoints except for of-token
    if (!queryString.includes("auth/") || queryString === "auth/of-token") {
      axiosConfig = {
        headers: {
          Authorization: "Bearer " + accessToken,
          //Action: action || urlPath,
          "Accept-Language": "en",
          //'ClientId': this.user.client,
        },
      };
    }

    try {
      const { data } = await axios.put(apiAddress + queryString, payload, axiosConfig);
      return data;
    } catch (error) {
      console.error(error);
      throw error;
    }
  }

  async deleteData(queryString: string, api: string = "main", isClient: boolean = false, body?: any) {
    let urlPath = window.location.pathname.toLowerCase();
    if (urlPath === "/") {
      urlPath = "/login";
    }

    const localUrls = ["127.0.0.1", "localhost"];
    let isLocalHost = localUrls.some((url) => this.user?.ofProviderUrl?.includes(url));

    const serverRoutingEnabled = this.user.tokenVirtualPath ? true : false;

    const apiAddressConfig = config.apiAddress[api];

    //for all apis except main
    //let apiAddress = apiAddressConfig.url + apiAddressConfig.virtualPath;
    let apiAddress = this.user.ofProviderUrl + apiAddressConfig.virtualPath;

    if (serverRoutingEnabled && api !== "pro_client") {
      apiAddress = this.user.ofProviderUrl + apiAddressConfig.suffixPath + apiAddressConfig.virtualPath;
    }

    //accessToken
    let accessToken = isClient ? this.user.ofAccessTokenClient : this.user.ofAccessToken;

    if (api === "main") {
      apiAddress = apiAddressConfig.url + apiAddressConfig.virtualPath;
      accessToken = this.user.accessToken;
    }

    if (isLocalHost) {
      apiAddress = apiAddressConfig.url + apiAddressConfig.virtualPath;
    }

    let axiosConfig: AxiosRequestConfig = {};

    //bypass auth for auth endpoints except for of-token
    if (!queryString.includes("auth/") || queryString === "auth/of-token") {
      axiosConfig = {
        headers: {
          Authorization: "Bearer " + accessToken,
          //Action: action || urlPath,
          "Accept-Language": "en",
          //'ClientId': this.user.client,
        },
      };
    }

    try {
      const requestConfig = body !== undefined ? { ...axiosConfig, data: body } : axiosConfig;
      return await axios.delete(apiAddress + queryString, requestConfig);
    } catch (error) {
      console.error(error);
      throw error;
    }
  }

  public isRouteReadOnly = (path) => {
    let readOnly = false;
    let isSet = false;

    this.user.routes.map((routeGroup) => {
      let route = routeGroup.userRoutes.find((route) => route.linkTo === path);

      if (route && !isSet) {
        const rights = _.get(route, "rights", "R");

        if (rights === "R") {
          readOnly = true;
        }
        isSet = true;
      }
    });

    return !isSet ? false : readOnly;
  };

  public getCustomLogo = (clientLogo = true) => {
    let logo = null;
    let logoType = "clientLogo";
    let allowDevLogo = true;

    if (!clientLogo) {
      logoType = "partnerLogo";
    } else {
      // Check flag.
      if (allowDevLogo) {
        // Check if you are running locally.
        if (process.env.NODE_ENV !== "production") {
          // Check the config to see if overrides are enabled, then we assume you are in debug mode.
          if (config.configOverride) {
            if (config.environmentName === "Azure Dev") {
              logoType = "devLogo";
            } else {
              logoType = "debugLogo";
            }
          }
        }
        // If there is no config override, assume it's DEV mode.
        if (!config.configOverride) {
          logoType = "devLogo";
        }
      }
    }

    try {
      logo = require("../images/customLogo/" + logoType + ".svg");
    } catch (ex) {
      try {
        logo = require("../images/customLogo/" + logoType + ".png");
      } catch (ex) {
        // console.log('Error importing ' + clientLogo)
      }
    }

    return logo;
  };
  public getPartnerLogo = () => {
    let logo = null;
    const partner = this.user?.clientRef;

    if (partner) {
      try {
        logo = require("../images/customLogo/partners/" + partner + ".svg");
      } catch (ex) {
        try {
          logo = require("../images/customLogo/partners/" + partner + ".png");
        } catch (ex) {
          // console.log('Error importing ' + clientLogo)
        }
      }
    }

    return logo;
  };
}
